import React, { Fragment, useState, useEffect, useMemo } from 'react'
import useAuth from '../hooks/useAuth'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import moment from 'moment'
import Modal from '../components/Modal'
import {
  Menu,
  MenuButton,
  MenuItems,
  MenuItem,
  Transition,
} from '@headlessui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCheck,
  faChevronUp,
  faChevronDown,
  faFilter,
} from '@fortawesome/free-solid-svg-icons'
import Datepicker from 'react-tailwindcss-datepicker'
import { TimePicker } from '@hilla/react-components/TimePicker.js'
import ShowAvailability from './ShowAvailability'

function BookSession({
  claimData = null,
  sessions = [],
  setBookSession,
  insurerHistory,
}) {
  const { auth, setShowLoader, createInfo } = useAuth()
  const axios = useAxiosPrivate()
  const [availabilities, setAvailabilities] = useState([])
  const [availabilitiesCache, setAvailabilitiesCache] = useState([])
  const [selectedTimeSlot, setSelectedTimeSlot] = useState({})
  const [selectedTimeOfDay, setSelectedTimeOfDay] = useState('00:00-23:59')
  const [selectedPractitioner, setSelectedPractitioner] = useState(0)
  const [availabilitiesLoaded, setAvailabilitiesLoaded] = useState(false)
  const [showAllAvailable, setShowAllAvailable] = useState(false)
  const [bookMultipleSessions, setBookMultipleSessions] = useState(false)
  const [videoSession, setVideoSession] = useState(false)
  const [dateError, setDateError] = useState(false)
  const [selectedDaysError, setSelectedDaysError] = useState(false)
  const [hasSessions, setHasSessions] = useState(true)
  const [sortCriteria, setSortCriteria] = useState('date')
  const [bookingData, setBookingData] = useState({})
  const [availabilityPercentages, setAvailabilityPercentages] = useState({})
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [startDate, setStartDate] = useState(moment().toDate())
  const [endDate, setEndDate] = useState(moment().add(7, 'days').toDate())
  const [selectedDays, setSelectedDays] = useState([])
  const [selectedHour, setSelectedHour] = useState('')
  const [customStartHour, setCustomStartHour] = useState('')
  const [customEndHour, setCustomEndHour] = useState('')
  const [selectedDate, setSelectedDate] = useState('')
  const [selectedTime, setSelectedTime] = useState('')
  const [error, setError] = useState('')
  const [isAssignedPractitioner, setIsAssignedPractitioner] = useState(false)

  const sortOptions = [
    { id: 1, name: 'Order By Date', value: 'date' },
    { id: 2, name: 'Order By Percentage Booked', value: 'percentageBooked' },
  ]

  const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
  const isAllSelected = selectedDays.length === allDays.length

  const [selectedSortOption, setSelectedSortOption] = useState(sortOptions[0])
  // Modal
  const [showModal, setShowModal] = useState(false)
  const [modalTitle, setModalTitle] = useState('')

  const [session, setSession] = useState({
    date: moment().format('YYYY-MM-DD'),
    service: 0,
    allUsers: true,
  })

  const loadAvailability = async () => {
    if (!startDate || !endDate) {
      setDateError(true)
      return
    }
    if (selectedDays.length === 0) {
      setSelectedDaysError(true)
      return
    }
    setBookMultipleSessions(false)
    setVideoSession(false)
    setDateError(false)
    setAvailabilitiesLoaded(false)
    setSelectedPractitioner(0)
    setShowAllAvailable(true)
    setSelectedTimeSlot([])
    setShowLoader(true)

    const { start, end } = getTimeRange()
    const formattedStartDate = moment(startDate).format('YYYY-MM-DD')
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD')
    try {
      const response = await axios.post(
        `/availabilities/${formattedStartDate}/${formattedEndDate}/${claimData.service.id}/${session.allUsers}/${start}/${end}`,
        { selected_days: selectedDays }
      )
      if (!response.data.error) {
        setAvailabilitiesLoaded(true)
        const loadedAvailabilities = response.data.result
        await calculateAndGroupAvailabilities(loadedAvailabilities)
      }
    } catch (error) {
      console.error(error)
    }
    setShowLoader(false)
  }

  const fetchRecurringAvailability = async () => {
    if (!startDate) {
      setDateError(true)
      return
    }
    if (selectedDays.length === 0) {
      setSelectedDaysError(true)
      return
    }

    setDateError(false)
    setSelectedPractitioner(0)
    setAvailabilitiesLoaded(true)
    setShowAllAvailable(true)
    setSelectedTimeSlot([])
    setShowLoader(true)

    let remaining = claimData.session_count - claimData.used_sessions.length
    const formattedStartDate = moment(startDate).format('YYYY-MM-DD')
    const { start, end } = getTimeRange()

    try {
      const response = await axios.post(
        `/availabilities/recurring/${formattedStartDate}/${claimData.service.id}/${session.allUsers}/${start}/${end}`,
        {
          selected_days: selectedDays,
          remaining_sessions: remaining,
        }
      )

      if (!response.data.error) {
        setAvailabilitiesLoaded(true)
        const loadedAvailabilities = response.data.result
        await calculateAndGroupAvailabilities(loadedAvailabilities)
      }
    } catch (error) {
      console.error('Error fetching recurring availability:', error)
    }

    setShowLoader(false)
  }

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedDays([])
    } else {
      setSelectedDays(allDays)
    }
  }

  const toggleSelectedDays = (day) => {
    const dayIndex = selectedDays.indexOf(day)
    if (dayIndex === -1) {
      setSelectedDays([...selectedDays, day])
    } else {
      setSelectedDays(selectedDays.filter((d, i) => i !== dayIndex))
    }
  }

  const getTimeRange = () => {
    if (selectedTimeOfDay) {
      if (selectedTimeOfDay === 'custom') {
        if (!customEndHour && customStartHour) {
          const startHourInt = parseInt(customStartHour.split(':')[0], 10)
          const defaultEndHour =
            startHourInt + 1 <= 23 ? `${startHourInt + 1}:00` : '23:59'
          setCustomEndHour(defaultEndHour)
          return { start: customStartHour, end: defaultEndHour }
        }
        return {
          start: customStartHour || '00:00',
          end: customEndHour || '23:59',
        }
      }
      return timeOfDayRanges[selectedTimeOfDay]
    }
    if (selectedHour !== '') {
      const hour = parseInt(selectedHour, 10)
      return { start: `${hour}:00`, end: `${hour + 1}:00` }
    }
    return { start: '00:00', end: '23:59' }
  }

  const timeOfDayRanges = {
    '00:00-23:59': { start: '00:00', end: '23:59' },
    '00:00-11:59': { start: '00:00', end: '11:59' },
    '12:00-16:59': { start: '12:00', end: '16:59' },
    '17:00-23:59': { start: '17:00', end: '23:59' },
  }

  const handleBooking = () => {
    if (!selectedDate || !selectedTime) {
      setError('Both date and time are required.')
      return
    }
    setError('')

    const startDateTime = moment(`${selectedDate} ${selectedTime}`).format(
      'YYYY-MM-DD HH:mm'
    )
    addSession(auth.user, selectedTime, startDateTime)
  }

  const createBooking = () => {
    if (!selectedDate || !selectedTime) {
      setError('Both date and time are required.')
      return
    }
    setError('')
    const assignedPractitioner =
      claimData?.client_relationship?.assigned_practitioner?.[0]

    if (!assignedPractitioner) {
      setError('No assigned practitioner available.')
      return
    }

    const startDateTime = moment(
      `${selectedDate} ${selectedTime}`,
      'YYYY-MM-DD HH:mm'
    ).format('YYYY-MM-DD HH:mm')

    addSession(assignedPractitioner, selectedTime, startDateTime)
  }

  const sortPractitionersByBookingPercentage = (practitioners, percentages) => {
    return practitioners.slice().sort((a, b) => {
      const percentageA = percentages[a.user.id] || 0
      const percentageB = percentages[b.user.id] || 0
      return percentageA - percentageB
    })
  }

  const sortPractitionersByDate = (practitioners) => {
    return practitioners.slice().sort((a, b) => {
      const earliestSlotA = Math.min(
        ...a.slots.map((slot) => new Date(slot).getTime())
      )
      const earliestSlotB = Math.min(
        ...b.slots.map((slot) => new Date(slot).getTime())
      )
      return earliestSlotA - earliestSlotB
    })
  }

  const sortAvailabilities = (availabilities, percentages) => {
    if (sortCriteria === 'date') {
      return sortPractitionersByDate(availabilities)
    } else {
      return sortPractitionersByBookingPercentage(availabilities, percentages)
    }
  }

  const sortedAvailabilities = useMemo(() => {
    return sortAvailabilities(availabilities, availabilityPercentages)
  }, [sortCriteria, availabilities, availabilityPercentages])

  const calculateAndGroupAvailabilities = async (loadedAvailabilities) => {
    try {
      const percentageResponse = await axios.post(
        `/claims/calculate-availability`,
        session
      )
      if (!percentageResponse.data.error) {
        setAvailabilityPercentages(percentageResponse.data)
        const sortedAvailabilities = sortAvailabilities(
          loadedAvailabilities,
          percentageResponse.data
        )
        const groupedAvailabilities =
          groupAvailabilitiesByDay(sortedAvailabilities)
        setAvailabilities(groupedAvailabilities)
        setAvailabilitiesCache(groupedAvailabilities)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const groupAvailabilitiesByDay = (availabilities) => {
    let arr = {}
    let practitioners = []
    availabilities.forEach((a) => {
      let days = {}
      let timeSlots = {}
      a.slots.forEach((slot) => {
        const formattedDate = moment(slot).format('DD/MM/YYYY')
        if (formattedDate === 'Invalid date') {
          days[slot] = [slot]
          return
        }
        if (days[formattedDate]) {
          days[formattedDate].push(slot)
        } else {
          days[formattedDate] = [slot]
        }

        if (!timeSlots[formattedDate]) {
          timeSlots[formattedDate] = 0
        }
      })
      arr[a.user.name] = timeSlots
      a.days = days
      practitioners.push(a)
    })
    setSelectedTimeSlot(arr)
    return practitioners
  }

  const bookSessionClicked = () => {
    setModalTitle(`Cannot Book Session`)
    setShowModal(true)
  }

  const resetModal = () => {
    setShowModal(false)
  }

  const checkHasSessions = () => {
    const sessionsRemaining =
      claimData.session_count - claimData.used_sessions.length

    if (sessionsRemaining > 0) {
      return true
    }

    return false
  }

  const updateAvailabilities = (checked) => {
    if (!checked) {
      setAvailabilities(availabilitiesCache)
      setAvailabilitiesCache(availabilitiesCache)
      return
    }

    let filteredAvailabilities = availabilities.filter((availability) => {
      return availability.user.video_sessions === 1
    })

    setAvailabilities(filteredAvailabilities)
    setAvailabilitiesCache(filteredAvailabilities)
    return
  }

  const calcPercentAvailable = async () => {
    setShowLoader(true)
    try {
      const response = await axios.post(
        `/claims/calculate-availability`,
        session
      )

      if (!response.data.error) {
        setAvailabilityPercentages(response.data)
      } else {
        createInfo('error', 'Could not fetch practitioner availability.')
      }
    } catch (error) {
      createInfo('error', 'An error occurred while processing your request.')
    }
    setShowLoader(false)
  }

  const handleSortChange = (option) => {
    setSelectedSortOption(option)
    setSortCriteria(option.value)
  }

  const NoSessionsElement = () => {
    const add = async (e) => {
      e.preventDefault()
      resetModal()
      setShowLoader(true)
      const data = {
        date: moment(bookingData.time).format('YYYY-MM-DD'),
        start: moment(bookingData.time).format('HH:mm'),
        end: moment(bookingData.time)
          .add(claimData.service.default_length, 'minutes')
          .format('HH:mm'),
        user: bookingData.user,
        service: claimData.service,
      }

      try {
        const response = await axios.post(
          `/claims/${claimData.id}/session/add`,
          data
        )
        if (!response.data.error) {
          sessions.push(response.data.result)
          createInfo(
            'success',
            `Session booked for ${moment(bookingData.time).format(
              'DD/MM/YYYY HH:mm'
            )}`
          )
          setBookSession(false)
        } else {
          const errorMessage =
            response.data.message || 'An unexpected error occurred.'
          createInfo('error', errorMessage)
        }
      } catch (error) {
        console.log(error)
      }
      setShowLoader(false)
    }
    return (
      <form onSubmit={add} className="justify-center flex">
        <div className="mb-3">
          <p className="text-lg font-bold my-8 text-center">
            This client has no remaining sessions.
          </p>
          <div className="flex mt-2 w-full">
            {(auth.user.type_id === 1 || auth.user.type_id === 2) && (
              <button className="btn red mt-4 mr-4 w-1/2">Book Session</button>
            )}
            <button
              type="button"
              className={
                auth.user.type_id === 1 || auth.user.type_id === 2
                  ? 'btn mt-4 w-1/2 bg-blue-500'
                  : 'btn mt-4 w-full'
              }
              onClick={resetModal}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    )
  }

  const ModalBody = () => {
    if (!hasSessions) {
      return <NoSessionsElement />
    }
  }

  const addSession = async (user, day, time) => {
    if (time === 0) {
      createInfo('error', 'Please select a time slot')
      return
    }
    setShowLoader(true)
    let bookingInfo = {
      user: user,
      time: time,
    }

    setBookingData(bookingInfo)
    let canBook = checkHasSessions()
    if (!canBook) {
      setShowLoader(false)
      setHasSessions(false)
      bookSessionClicked()
      return
    }
    const data = {
      date: moment(bookingInfo.time).format('YYYY-MM-DD'),
      start: moment(bookingInfo.time).format('HH:mm'),
      end: moment(bookingInfo.time)
        .add(claimData.service.default_length, 'minutes')
        .format('HH:mm'),
      user: user,
      service: claimData.service,
    }

    if (videoSession && user.video_sessions === 1) {
      data.video_session = 1
    }

    try {
      const response = await axios.post(
        `/claims/${claimData.id}/session/add`,
        data
      )
      if (!response.data.error) {
        sessions.push(response.data.result)
        createInfo(
          'success',
          `Session booked for ${moment(bookingInfo.time).format(
            'DD/MM/YYYY HH:mm'
          )}`
        )
        setBookSession(false)
      } else {
        const errorMessage =
          response.data.message || 'An unexpected error occurred.'
        createInfo('error', errorMessage)
      }
    } catch (error) {
      console.log(error)
    }
    setShowLoader(false)
  }

  useEffect(() => {
    if (availabilities.length > 0) {
      const groupedAvailabilities =
        groupAvailabilitiesByDay(sortedAvailabilities)

      if (
        JSON.stringify(groupedAvailabilities) !== JSON.stringify(availabilities)
      ) {
        setAvailabilities(groupedAvailabilities)
        setAvailabilitiesCache(groupedAvailabilities)
      }
    }
  }, [sortedAvailabilities, availabilities])

  useEffect(() => {
    if (claimData && auth?.user?.id) {
      const loggedInUserId = auth.user.id
      const assignedPractitioners =
        claimData.client_relationship?.assigned_practitioner || []

      const isAssigned = assignedPractitioners.some(
        (p) => p.id === loggedInUserId
      )
      setIsAssignedPractitioner(isAssigned)
    }
  }, [claimData, auth])

  return (
    <div>
      {showModal && (
        <Modal title={modalTitle} body={<ModalBody />} show={resetModal} />
      )}
      <div className="grid grid-cols-2 gap-4">
        <div className="border-r-2">
          <div className="max-w-lg">
            <h3 className="font-bold text-lg mb-2">Book a Session</h3>

            <div className="mb-3">
              <label
                htmlFor="service"
                className="block font-medium text-gray-600 text-sm"
              >
                Service
              </label>
              <input
                type="text"
                disabled
                value={claimData.service.title}
                className="px-4 py-2 block w-full border border-gray-300 rounded-md shadow-md"
              />
            </div>
          </div>
          <div>
            <div>
              <div className="max-w-lg">
                <div className="mb-3">
                  <label
                    htmlFor="date"
                    className="block font-medium text-gray-600 text-sm"
                  >
                    Week Start
                  </label>
                  <div className="mt-1 relative rounded-md border p-0.5 border-gray-300 shadow-md">
                    <Datepicker
                      value={{ startDate, endDate }}
                      onChange={(value) => {
                        const { startDate: start, endDate: end } = value
                        setStartDate(start)
                        setEndDate(end)
                      }}
                      startDate={startDate}
                      endDate={endDate}
                      selectsRange
                      useRange={false}
                      primaryColor={'blue'}
                      minDate={moment().toDate()}
                      inline
                    />
                    {dateError && (
                      <p className="text-red-500 text-sm mt-1">
                        This field is required
                      </p>
                    )}
                  </div>
                </div>
                <div className="mb-3">
                 <div className='flex items-center justify-between'>
                    <label htmlFor="daysOfWeek" className="block font-medium text-gray-600 text-sm">
                      Available Days
                    </label>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="selectAll"
                        className="mr-2 field"
                        checked={isAllSelected}
                        onChange={toggleSelectAll}
                      />
                      <label htmlFor="selectAll" className="font-medium text-gray-600 text-sm">Select All</label>
                    </div>
                 </div>
                  <div className="mt-1 relative ml-1">               
                    {/* Select All Checkbox */}
                    {allDays.map((day) => (
                      <div key={day} className="flex items-center">
                        <input
                          type="checkbox"
                          name="daysOfWeek"
                          value={day}
                          className="mr-2 field"
                          onChange={(e) => toggleSelectedDays(e.target.value)}
                          checked={selectedDays.includes(day)}
                        />
                        <label htmlFor={day}>{day.charAt(0).toUpperCase() + day.slice(1)}</label>
                      </div>
                    ))}
                  </div>

                  {selectedDaysError && (
                    <p className="text-red-500 text-sm mt-1">Please select at least one day</p>
                  )}
                </div>
                <div className="mb-3">
                  <label
                    htmlFor="timeOfDay"
                    className="block font-medium text-gray-600 text-sm"
                  >
                    Time of day
                  </label>
                  <select
                    className="px-2 py-2 block w-full border border-gray-300 rounded-md shadow-md"
                    value={selectedTimeOfDay}
                    onChange={(e) => {
                      setSelectedHour('')
                      setSelectedTimeOfDay(e.target.value)
                      setCustomStartHour('')
                      setCustomEndHour('')
                    }}
                    id="timeOfDay"
                  >
                    <option value="">Select Time of Day</option>
                    <option value="00:00-23:59">All Day</option>
                    <option value="00:00-11:59">Morning</option>
                    <option value="12:00-16:59">Afternoon</option>
                    <option value="17:00-23:59">Evening</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>

                {selectedTimeOfDay === 'custom' && (
                  <div className="mb-3">
                    <label className="block font-medium text-gray-600 text-sm">
                      Select Custom Time Range
                    </label>
                    <div className="flex space-x-2">
                      <TimePicker
                        label="Start Time"
                        value={customStartHour}
                        onValueChanged={(e) => {
                          setCustomStartHour(e.detail.value)
                          setCustomEndHour('')
                        }}
                        min="07:00"
                        step={60 * 30}
                        className="mt-4"
                      />
                      <TimePicker
                        label="End Time"
                        value={customEndHour}
                        onValueChanged={(e) => setCustomEndHour(e.detail.value)}
                        step={60 * 30}
                        min={customStartHour}
                        max="23:59"
                        disabled={!customStartHour}
                        className="mt-4"
                      />
                    </div>
                  </div>
                )}
                <div className="w-full mr-2 flex items-center mb-2">
                  <div className="relative mr-2">
                    <input
                      type="checkbox"
                      id="get_all"
                      onChange={(e) => {
                        setAvailabilitiesLoaded(false)
                        setSelectedPractitioner(0)
                        setShowAllAvailable(false)
                        setSession((prev) => ({
                          ...prev,
                          allUsers: e.target.checked,
                        }))
                      }}
                      placeholder="All Practitioners"
                      className="field"
                      checked={session.allUsers}
                    />
                  </div>
                  <label
                    htmlFor="get_all"
                    className="block font-medium text-gray-600 text-sm"
                  >
                    All Practitioners
                  </label>
                </div>

                <button className="btn" onClick={loadAvailability}>
                  Load Availability
                </button>
              </div>
            </div>
          </div>
        </div>
        {auth.user &&
          (isAssignedPractitioner || [1, 2].includes(auth.user.type_id)) && (
            <div className="">
              <h2 className="text-lg font-semibold mb-4">
                {[1, 2].includes(auth.user.type_id) &&
                claimData.client_relationship?.assigned_practitioner[0]
                  ? `Create a Custom Session with ${claimData.client_relationship?.assigned_practitioner[0].name}`
                  : `Create a Custom Session`}
              </h2>

              <div className="my-3">
                <label
                  htmlFor="service"
                  className="block font-medium text-gray-600 text-sm"
                >
                  Service
                </label>
                <input
                  type="text"
                  disabled
                  value={claimData?.service?.title || 'N/A'}
                  className="px-4 py-2 block w-2/3 border border-gray-300 rounded-md shadow-md bg-gray-100"
                />
              </div>

              <div className="mb-3">
                <label className="block font-medium text-gray-600 text-sm">
                  Select Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="px-4 py-2 block w-2/3 border border-gray-300 rounded-md shadow-md"
                />
              </div>

              <div className="mb-3">
                <label className="block font-medium text-gray-600 text-sm">
                  Start Time <span className="text-red-500">*</span>
                </label>
                <TimePicker
                  value={selectedTime}
                  onValueChanged={(e) => {
                    if (e.detail.value) {
                      setSelectedTime(e.detail.value)
                    }
                  }}
                  min="07:00"
                  step={60 * 15}
                  className="w-2/3 shadow-md rounded-lg"
                />
              </div>

              {error && <p className="text-red-500 text-sm">{error}</p>}

              {claimData.client_relationship?.assigned_practitioner?.length >
                0 &&
                claimData.client_relationship?.assigned_practitioner[0]
                  ?.video_sessions === 1 && (
                  <div className=" w-full mr-2 flex items-center mb-2">
                    <div className="relative mr-2">
                      <input
                        type="checkbox"
                        id="videoSession"
                        onChange={(e) => {
                          setVideoSession(e.target.checked)
                        }}
                        placeholder="Show only Video Session Practitioners"
                        className="field"
                        checked={videoSession}
                      />
                    </div>
                    <label
                      htmlFor="videoSession"
                      className="block font-medium text-gray-600 text-sm"
                    >
                      Video Session
                    </label>
                  </div>
                )}
              <button
                onClick={() => {
                  if (isAssignedPractitioner) {
                    handleBooking()
                  } else {
                    createBooking()
                  }
                }}
                className="mt-4 px-4 py-2 bg-blue-500 text-white font-semibold rounded-md hover:bg-blue-700 transition text-sm"
              >
                Create Session
              </button>
            </div>
          )}
      </div>
      <div>
        {availabilities.length > 0 && availabilitiesLoaded && (
          <div className="border-t border-slate-200 mt-4">
            <h3 className="font-bold text-lg mb-2 mt-4">
              Available Practitioners ({availabilities.length})
            </h3>
            <div className="flex flex-col mr-2">
              <div className="w-full mr-2 flex items-center mb-2">
                <div className="relative mr-2">
                  <input
                    type="checkbox"
                    id="show_all"
                    onChange={(e) => {
                      setSelectedPractitioner(0)
                      setShowAllAvailable(e.target.checked)
                      if (e.target.checked) {
                        calcPercentAvailable()
                      }
                    }}
                    placeholder="Show all Available"
                    className="field"
                    checked={showAllAvailable}
                  />
                </div>
                <label
                  htmlFor="show_all"
                  className="block font-medium text-gray-600 text-sm"
                >
                  Show All Available Practitioners
                </label>
              </div>
              <div className="w-full mr-2 flex items-center mb-2">
                <div className="relative mr-2">
                  <input
                    type="checkbox"
                    id="show_recurring"
                    onChange={(e) => {
                      setBookMultipleSessions(e.target.checked)
                      setSelectedPractitioner(0)
                      if (e.target.checked) {
                        fetchRecurringAvailability()
                      } else {
                        loadAvailability()
                      }
                    }}
                    className="field"
                    defaultChecked={bookMultipleSessions}
                  />
                </div>
                <label
                  htmlFor="show_recurring"
                  className="block font-medium text-gray-600 text-sm"
                >
                  Book Multiple Sessions
                </label>
              </div>

              <div className=" w-full mr-2 flex items-center mb-2">
                <div className="relative mr-2">
                  <input
                    type="checkbox"
                    id="videoSession"
                    onChange={(e) => {
                      setVideoSession(e.target.checked)
                      updateAvailabilities(e.target.checked)
                    }}
                    placeholder="Show only Video Session Practitioners"
                    className="field"
                    checked={videoSession}
                  />
                </div>
                <label
                  htmlFor="videoSession"
                  className="block font-medium text-gray-600 text-sm"
                >
                  Video Session
                </label>
              </div>
              <div className="w-full">
                <div className="flex justify-end items-end space-x-2">
                  <div className="w-72">
                    <h2 className="block text-sm font-medium leading-6 text-gray-900">
                      Sort Options
                    </h2>
                    <Menu
                      as="div"
                      className="relative inline-block text-left w-full"
                    >
                      <div>
                        <MenuButton
                          className="flex items-center text-gray-900 font-medium leading-6 bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm hover:bg-gray-100 w-full justify-between"
                          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        >
                          <span className="mr-2">
                            {selectedSortOption.name}
                          </span>
                          <FontAwesomeIcon
                            icon={isDropdownOpen ? faChevronUp : faChevronDown}
                            className="h-5 w-5 text-gray-400"
                            aria-hidden="true"
                          />
                        </MenuButton>
                      </div>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <MenuItems className="absolute mt-2 w-full origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-md ring-1 ring-black ring-opacity-5 focus:outline-none">
                          <div className="p-4">
                            {sortOptions.map((option) => (
                              <MenuItem key={option.id}>
                                {({ focus }) => (
                                  <div
                                    onClick={() => handleSortChange(option)}
                                    className={`flex items-center cursor-pointer py-2 px-4 hover:bg-blue-400 rounded-lg ${
                                      focus
                                        ? 'bg-indigo-600 text-white'
                                        : 'text-gray-900'
                                    } ${
                                      selectedSortOption.value === option.value
                                        ? 'font-semibold'
                                        : ''
                                    }`}
                                  >
                                    <div className="block truncate">
                                      {option.name}
                                    </div>
                                    {selectedSortOption.value ===
                                      option.value && (
                                      <div className="ml-auto text-indigo-600">
                                        <FontAwesomeIcon
                                          icon={faCheck}
                                          className="h-5 w-5"
                                          aria-hidden="true"
                                        />
                                      </div>
                                    )}
                                  </div>
                                )}
                              </MenuItem>
                            ))}
                          </div>
                        </MenuItems>
                      </Transition>
                    </Menu>
                  </div>
                </div>
              </div>
              {!showAllAvailable && (
                <div className="w-[300px] relative mr-4">
                  <div className="mb-3">
                    <label
                      htmlFor="service"
                      className="block font-medium text-gray-600 text-sm"
                    >
                      Practitioner
                    </label>
                    <select
                      defaultValue={selectedPractitioner}
                      className="px-2 py-2 block w-full border border-gray-300 rounded-md shadow-md"
                      required
                      onChange={(e) =>
                        setSelectedPractitioner(JSON.parse(e.target.value))
                      }
                      id="service"
                    >
                      <option disabled value="0">
                        Select a Practitioner
                      </option>
                      {availabilities.map((availability, index) => (
                        <option
                          key={index}
                          value={JSON.stringify(availability)}
                        >
                          {availability.user.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              {selectedPractitioner > 0 && (
                <ShowAvailability
                  data={selectedPractitioner}
                  specificNeeds={claimData.specialities}
                  addSession={addSession}
                  availabilityPercentages={availabilityPercentages}
                  bookMultipleSessions={bookMultipleSessions}
                  selectedTimeSlot={selectedTimeSlot}
                  setSelectedTimeSlot={setSelectedTimeSlot}
                  claimData={claimData}
                  setBookSession={setBookSession}
                  startDate={startDate}
                  endDate={endDate}
                />
              )}

              {showAllAvailable &&
                sortedAvailabilities.map((availability, index) => {
                  return (
                    <ShowAvailability
                      data={availability}
                      specificNeeds={claimData.specialities}
                      addSession={addSession}
                      availabilityPercentages={availabilityPercentages}
                      bookMultipleSessions={bookMultipleSessions}
                      selectedTimeSlot={selectedTimeSlot}
                      setSelectedTimeSlot={setSelectedTimeSlot}
                      claimData={claimData}
                      setBookSession={setBookSession}
                      startDate={startDate}
                      endDate={endDate}
                    />
                  )
                })}
            </div>
          </div>
        )}
        {availabilities.length <= 0 && availabilitiesLoaded && (
          <h3 className="font-bold text-lg mb-2 mt-4">
            No Available Practitioners
          </h3>
        )}
      </div>
    </div>
  )
}

export default BookSession
